import type { ScoredRoute } from '../components/RouteResults';

/**
 * Opens SimBrief with pre-populated flight plan data
 * @param route - The scored route to plan in SimBrief
 * @param aircraftType - The aircraft type for the flight
 */
export function openSimBrief(route: ScoredRoute, aircraftType: string): void {
  // SimBrief dispatch URL format
  // https://www.simbrief.com/system/dispatch.php?orig=KSFO&dest=KLAS&type=A320&route=...
  
  const params = new URLSearchParams({
    orig: route.departure,
    dest: route.arrival,
    type: aircraftType,
  });

  // Add route string if available
  if (route.routeString) {
    params.append('route', route.routeString);
  }

  const url = `https://www.simbrief.com/system/dispatch.php?${params.toString()}`;
  
  // Open in new tab
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Formats a route string for display and copying
 * @param route - The scored route
 * @returns Formatted route string
 */
export function formatRouteString(route: ScoredRoute): string {
  if (route.routeString) {
    return route.routeString;
  }
  
  // Fallback: simple direct route
  return `${route.departure} DCT ${route.arrival}`;
}

/**
 * Copies route string to clipboard
 * @param route - The scored route
 * @returns Promise that resolves when copy is complete
 */
export async function copyRouteToClipboard(route: ScoredRoute): Promise<void> {
  const routeString = formatRouteString(route);
  
  try {
    await navigator.clipboard.writeText(routeString);
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = routeString;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

/**
 * Generates a SimBrief URL without opening it
 * @param route - The scored route
 * @param aircraftType - The aircraft type
 * @returns The SimBrief dispatch URL
 */
export function getSimBriefUrl(route: ScoredRoute, aircraftType: string): string {
  const params = new URLSearchParams({
    orig: route.departure,
    dest: route.arrival,
    type: aircraftType,
  });

  if (route.routeString) {
    params.append('route', route.routeString);
  }

  return `https://www.simbrief.com/system/dispatch.php?${params.toString()}`;
}
